import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  BarChart3, Star, MapPin, Search, Globe, Clock, TrendingUp,
  Smartphone, RefreshCw, Eye
} from 'lucide-react';
import axios from 'axios';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 20px;`;
const Header = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 16px;`;
const Title = styled.h1`font-size: 22px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;

const StatsGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px;`;
const StatCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px;
  padding: 16px; display: flex; flex-direction: column; gap: 6px;
`;
const StatLabel = styled.div`font-size: 10px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; display: flex; align-items: center; gap: 6px;`;
const StatValue = styled.div`font-size: 24px; font-weight: 900; color: ${p => p.$color || 'var(--text-primary)'};`;
const StatSub = styled.div`font-size: 10px; color: var(--text-muted);`;

const SectionTitle = styled.h2`
  font-size: 15px; font-weight: 700; color: var(--text-primary);
  display: flex; align-items: center; gap: 8px; margin: 0;
  padding-bottom: 8px; border-bottom: 1px solid var(--border);
`;

const Table = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px; overflow: hidden;
`;
const TRow = styled.div`
  display: grid; grid-template-columns: 50px 1fr 120px 80px 120px;
  padding: 10px 16px; align-items: center; gap: 8px;
  border-bottom: 1px solid var(--border); font-size: 12px; color: var(--text-secondary);
  &:last-child { border-bottom: none; }
  &:first-child { font-weight: 700; color: var(--text-muted); text-transform: uppercase; font-size: 10px; background: var(--surface); }
`;

const ReviewRow = styled.div`
  display: grid; grid-template-columns: 50px 1fr 80px 80px 120px;
  padding: 10px 16px; align-items: center; gap: 8px;
  border-bottom: 1px solid var(--border); font-size: 12px; color: var(--text-secondary);
  &:last-child { border-bottom: none; }
  &:first-child { font-weight: 700; color: var(--text-muted); text-transform: uppercase; font-size: 10px; background: var(--surface); }
`;

const StarRating = styled.div`
  color: #fbbc04; font-size: 12px; letter-spacing: 1px;
`;

const PeriodBtns = styled.div`display: flex; gap: 6px;`;
const PeriodBtn = styled.button`
  padding: 6px 14px; border-radius: 8px; border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--border)'};
  background: ${p => p.$active ? 'var(--primary)' : 'var(--surface)'};
  color: ${p => p.$active ? '#fff' : 'var(--text-secondary)'};
  font-size: 11px; font-weight: 600; cursor: pointer;
`;

const RefreshBtn = styled.button`
  padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-secondary); font-size: 12px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 40vh; color: var(--text-muted); text-align: center;
  svg { opacity: .2; margin-bottom: 12px; }
`;

const TabBar = styled.div`display: flex; gap: 4px; background: var(--surface); border-radius: 10px; padding: 3px;`;
const Tab = styled.button`
  padding: 8px 16px; border-radius: 8px; border: none; font-size: 12px; font-weight: 600; cursor: pointer;
  background: ${p => p.$active ? 'var(--card-bg)' : 'transparent'};
  color: ${p => p.$active ? 'var(--text-primary)' : 'var(--text-muted)'};
  box-shadow: ${p => p.$active ? 'var(--shadow)' : 'none'};
  display: flex; align-items: center; gap: 6px;
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const GoogleAnalytics = ({ selectedDevice }) => {
  const [period, setPeriod] = useState('7d');
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    if (!selectedDevice?.device_id) return;
    setLoading(true);
    try {
      const r = await axios.get(`/api/google-scripts/analytics/${selectedDevice.device_id}?period=${period}`);
      if (r.data.success) {
        setStats(r.data.stats);
        setReviews(r.data.reviews || []);
        setSearches(r.data.searches || []);
      }
    } catch { /* no data yet */ }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStats(); }, [selectedDevice?.device_id, period]);

  if (!selectedDevice) {
    return <Page><EmptyState><Smartphone size={48} /><p>Sélectionnez un appareil</p></EmptyState></Page>;
  }

  const s = stats || {};

  return (
    <Page>
      <Header>
        <Title><BarChart3 size={22} style={{ color: '#4285f4' }} /> Google — Analytique</Title>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <PeriodBtns>
            {['24h', '7d', '30d', 'all'].map(p => (
              <PeriodBtn key={p} $active={period === p} onClick={() => setPeriod(p)}>{p}</PeriodBtn>
            ))}
          </PeriodBtns>
          <RefreshBtn onClick={fetchStats} disabled={loading}>
            <RefreshCw size={12} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          </RefreshBtn>
        </div>
      </Header>

      <TabBar>
        <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}><Globe size={12} /> Vue d'ensemble</Tab>
        <Tab $active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')}><Star size={12} /> Avis Google</Tab>
        <Tab $active={activeTab === 'searches'} onClick={() => setActiveTab('searches')}><Search size={12} /> Recherches SEO</Tab>
      </TabBar>

      {activeTab === 'overview' && (
        <>
          <StatsGrid>
            <StatCard>
              <StatLabel><Star size={12} /> Avis déposés</StatLabel>
              <StatValue $color="#fbbc04">{s.total_reviews || 0}</StatValue>
              <StatSub>note moy: {s.avg_rating || '—'}/5</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel><MapPin size={12} /> Lieux visités</StatLabel>
              <StatValue $color="#34a853">{s.total_places || 0}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel><Search size={12} /> Recherches SEO</StatLabel>
              <StatValue $color="#4285f4">{s.total_searches || 0}</StatValue>
              <StatSub>clics: {s.total_clicks || 0}</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel><Eye size={12} /> YouTube vues</StatLabel>
              <StatValue $color="#ff0000">{s.total_youtube_views || 0}</StatValue>
              <StatSub>durée totale: {s.total_watch_mins || 0}min</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel><Clock size={12} /> Sessions</StatLabel>
              <StatValue>{s.total_sessions || 0}</StatValue>
              <StatSub>durée moy: {s.avg_session_mins || 0}min</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel><TrendingUp size={12} /> Taux de succès</StatLabel>
              <StatValue $color="var(--success)">{s.success_rate || '0'}%</StatValue>
            </StatCard>
          </StatsGrid>
        </>
      )}

      {activeTab === 'reviews' && (
        <>
          <SectionTitle><Star size={16} /> Historique des avis Google</SectionTitle>
          <Table>
            <ReviewRow>
              <span>#</span><span>Lieu</span><span>Note</span><span>Gmail</span><span>Date</span>
            </ReviewRow>
            {reviews.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Aucun avis Google enregistré
              </div>
            ) : (
              reviews.slice(0, 30).map((rev, i) => (
                <ReviewRow key={rev.id || i}>
                  <span style={{ fontWeight: 600 }}>{i + 1}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{rev.place_name || '—'}</span>
                  <StarRating>{'★'.repeat(rev.rating || 0)}{'☆'.repeat(5 - (rev.rating || 0))}</StarRating>
                  <span style={{ fontSize: 10 }}>{rev.gmail_account || '—'}</span>
                  <span>{rev.created_at ? new Date(rev.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                </ReviewRow>
              ))
            )}
          </Table>
        </>
      )}

      {activeTab === 'searches' && (
        <>
          <SectionTitle><Search size={16} /> Historique des recherches SEO</SectionTitle>
          <Table>
            <TRow>
              <span>#</span><span>Requête</span><span>URL cliquée</span><span>Position</span><span>Date</span>
            </TRow>
            {searches.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Aucune recherche SEO enregistrée
              </div>
            ) : (
              searches.slice(0, 30).map((s, i) => (
                <TRow key={s.id || i}>
                  <span style={{ fontWeight: 600 }}>{i + 1}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.query || '—'}</span>
                  <span style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.clicked_url || '—'}</span>
                  <span>{s.position || '—'}</span>
                  <span>{s.created_at ? new Date(s.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                </TRow>
              ))
            )}
          </Table>
        </>
      )}
    </Page>
  );
};

export default GoogleAnalytics;
